// Bills Tracker Form
import React, { useState } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { Receipt, Plus } from 'lucide-react';

interface BillsTrackerFormProps {
  onAdd: (item: { title: string; dueDate?: string; notes?: string; metadata?: any }) => void;
}

export default function BillsTrackerForm({ onAdd }: BillsTrackerFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [billName, setBillName] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [autoPay, setAutoPay] = useState(false);
  const [notes, setNotes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!listName.trim()) {
      alert('Please give your bill tracker a name!');
      return;
    }
    if (!billName.trim() || !amount) return;

    const amountNum = parseFloat(amount);
    
    onAdd({
      title: `${billName.trim()} - $${amountNum.toFixed(2)}`,
      dueDate: dueDate || undefined,
      notes: notes || undefined,
      metadata: {
        type: 'bill',
        listName: listName.trim(),
        amount: amountNum,
        autoPay,
        billName: billName.trim()
      }
    });

    // Reset form
    setListName('');
    setBillName('');
    setAmount('');
    setDueDate('');
    setAutoPay(false);
    setNotes('');
    setIsOpen(false);
  };

  return (
    <>
      {/* Add Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="w-full md:w-auto px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation"
      >
        <Receipt className="w-5 h-5" />
        Add Bill
      </button>

      {/* Form Modal */}
      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add Bill"
        maxWidth="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Tracker Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Bill Tracker Name *
            </label>
            <input
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="e.g., March Bills, Household Expenses, Business Bills"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              required
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">Name this bill tracker to organize different sets of bills</p>
          </div>
          
          {/* Bill Name */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Bill Name *
            </label>
            <input
              type="text"
              value={billName}
              onChange={(e) => setBillName(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
              placeholder="e.g., Electric, Internet, Rent"
              required
              autoFocus
            />
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Amount ($) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500 text-lg font-bold"
              placeholder="0.00"
              required
              inputMode="decimal"
            />
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Due Date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-rose-500 text-base"
            />
          </div>

          {/* Auto-pay Toggle */}
          <div className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <input
              type="checkbox"
              id="autoPay"
              checked={autoPay}
              onChange={(e) => setAutoPay(e.target.checked)}
              className="w-5 h-5 rounded border-slate-600 bg-slate-900 text-rose-600 focus:ring-2 focus:ring-rose-500"
            />
            <label htmlFor="autoPay" className="text-slate-300 font-medium">
              Auto-pay enabled
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500 text-base resize-none"
              placeholder="Additional notes..."
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation text-lg md:text-base mt-6"
          >
            <Plus className="w-5 h-5" />
            Add Bill
          </button>
        </form>
      </ResponsiveModal>
    </>
  );
}
